import { useState, useRef, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, MessageCircle, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [programOpen, setProgramOpen] = useState(false);
  const programRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (programRef.current && !programRef.current.contains(event.target)) {
        setProgramOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Main nav links
  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/affiliates", label: "Affiliates" },
    { href: "/about", label: "About" },
  ];

  // Program dropdown items
  const programLinks = [
    { href: "/plan", label: "Specialty Therapies", description: "View current Dosevana pricing and options" },
    { href: "/start", label: "Start", description: "Begin your planning pathway" },
    { href: "/switch-to-rx", label: "Compare My Current Plan", description: "Upload a receipt or cart for a quote" },
    { href: "/plan", label: "Pricing & Plans", description: "View GLP-1 pricing options" }
  ];

  const handleChatClick = () => {
    if (window.$zoho && window.$zoho.salesiq) {
      if (window.$zoho.salesiq.floatwindow) {
        window.$zoho.salesiq.floatwindow.visible("show");
      }
      if (window.$zoho.salesiq.chat) {
        window.$zoho.salesiq.chat.start();
      }
      return;
    }
    // Fallback: Zoho SalesIQ not yet loaded — route to /start
    navigate("/start");
  };

  return (
    <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[#E2E8F0]" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <span className="font-playfair text-xl md:text-2xl font-bold text-[#507DBC]">
              MyGLP1Compare
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className="text-[#2D3748] hover:text-[#507DBC] font-medium transition-colors text-sm"
              data-testid="nav-home"
            >
              Home
            </Link>

            {/* Program Dropdown */}
            <div className="relative" ref={programRef}>
              <button
                onClick={() => setProgramOpen(!programOpen)}
                className="flex items-center gap-1 text-[#2D3748] hover:text-[#507DBC] font-medium transition-colors text-sm"
                data-testid="nav-program"
              >
                Pricing & Plans
                <ChevronDown className={`w-4 h-4 transition-transform ${programOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {programOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-[#E2E8F0] py-2 z-50">
                  {programLinks.map((link) => (
                    	  	  		  	<Link
                      key={link.href}
                      to={link.href}
                      className="block px-4 py-3 hover:bg-[#F8F9FA] transition-colors"
                      onClick={() => setProgramOpen(false)}
                      data-testid={`nav-plans-${link.href.replace("/", "")}`}
                    >
                      <span className="block font-medium text-[#2D3748] text-sm">{link.label}</span>
                      <span className="block text-xs text-[#718096]">{link.description}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>



            <Link
              to="/about"
              className="text-[#2D3748] hover:text-[#507DBC] font-medium transition-colors text-sm"
              data-testid="nav-about"
            >
              About
            </Link>

            <Link
              to="/affiliates"
              className="text-[#2D3748] hover:text-[#507DBC] font-medium transition-colors text-sm"
              data-testid="nav-affiliates"
            >
              Affiliates
            </Link>

            <Button
              variant="outline"
              className="border-[#507DBC] text-[#507DBC] hover:bg-[#507DBC] hover:text-white rounded-full px-5 text-sm"
              onClick={handleChatClick}
              data-testid="nav-chat-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Chat
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-[#2D3748]"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-4 border-t border-[#E2E8F0]" data-testid="mobile-menu">
            <div className="flex flex-col gap-2">
              <Link to="/" className="text-[#2D3748] hover:text-[#507DBC] font-medium py-2" data-testid="mobile-nav-home">
                Home
              </Link>
              
              {/* Mobile Program Section */}
              <div className="border-t border-[#E2E8F0] pt-2 mt-2">
                <p className="text-xs text-[#718096] uppercase tracking-wide mb-2">Pricing & Plans</p>
                {programLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="block text-[#2D3748] hover:text-[#507DBC] font-medium py-2 pl-2"
                    data-testid={`mobile-plans-${link.href.replace("/", "")}`}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>

              <div className="border-t border-[#E2E8F0] pt-2 mt-2">
                <Link to="/about" className="block text-[#2D3748] hover:text-[#507DBC] font-medium py-2" data-testid="mobile-nav-about">
                  About
                </Link>
                <Link to="/affiliates" className="block text-[#2D3748] hover:text-[#507DBC] font-medium py-2" data-testid="mobile-nav-affiliates">
                  Affiliates
                </Link>
              </div>

              <Button
                variant="outline"
                className="border-[#507DBC] text-[#507DBC] hover:bg-[#507DBC] hover:text-white rounded-full w-full mt-4"
                onClick={handleChatClick}
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Chat with us
              </Button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
